import type { QuestionType } from "@/lib/api";

// Mirror of backend/models/enums.py `TRUE_FALSE_OPTIONS` (CP-7): the only two
// answers a true/false question has, in the order the candidate ALWAYS sees
// them — the server emits this pair unshuffled, because the shuffle exists to
// hide a correct-first list and a constant pair leaks nothing. The recruiter's
// key is stored verbatim as one of these two strings in `correct_answer`.
export const TRUE_FALSE_OPTIONS = ["True", "False"] as const;
export type TrueFalseAnswer = (typeof TRUE_FALSE_OPTIONS)[number];

export function isTrueFalseAnswer(value: string | null | undefined): value is TrueFalseAnswer {
  return value === "True" || value === "False";
}

/**
 * The full option list of a single-correct question, for the recruiter-side
 * reviews (results Detail, the editor's question panel). `mcq` keeps its
 * distractors in `other_answers`; `true_false` stores none — the pair above
 * is the option set — so building `[correct, ...others]` for it would list only
 * the key and flag the candidate's other pick as "off-list".
 */
export function singleChoiceOptions(
  type: QuestionType,
  correctAnswer: string | null | undefined,
  otherAnswers: string[] | null | undefined,
): string[] {
  if (type === "true_false") return [...TRUE_FALSE_OPTIONS];
  return [correctAnswer ?? "", ...(otherAnswers ?? [])];
}

/**
 * Which entry of `singleChoiceOptions` is the key. `mcq` lists correct-first,
 * but the true/false pair is a constant in candidate order — so "first option
 * is correct" marked "True" on every T/F question, a "False" key included
 * (walkthrough #19, 2026-09-29). -1 when a T/F key is missing or malformed.
 */
export function singleChoiceKeyIndex(
  type: QuestionType,
  correctAnswer: string | null | undefined,
): number {
  if (type === "true_false") {
    return isTrueFalseAnswer(correctAnswer) ? TRUE_FALSE_OPTIONS.indexOf(correctAnswer) : -1;
  }
  return 0;
}

/**
 * **`open_ended` is called "Written" — everywhere, recruiter and candidate.**
 * B18 (2026-09-12 browser QA) found FIVE names for one type in the shipped UI:
 * "Add open-ended" (create button), "Open ended" (badges, attempt header,
 * skills BY FORMAT), "Open" (template import TYPE column), "Written"
 * (Tests-list card) and "Written answer" (candidate review). A recruiter
 * reading two of those screens could not tell they were the same thing.
 *
 * "Written" won because it was already the candidate-facing word AND the word
 * every existing sentence uses ("a written answer is waiting for your score",
 * in ~5 places) — so one noun now covers labels and prose, rather than labels
 * agreeing with each other while disagreeing with the copy around them.
 * `Review.tsx` says "Written answer" on the candidate side: the same noun, as
 * a full phrase naming what they submitted rather than a type badge.
 *
 * A new surface takes the label from a map here. If a genuinely compact form
 * is needed (the import dialog's TYPE column), it is still "Written" — the
 * noun does not get re-invented per screen. (Leo, 2026-09-12.)
 *
 * Display label per question type, for the recruiter-side results surfaces.
 * ONE map: CP-7 added `true_false` while two hand-copied maps existed, and only
 * the AI summary card learned it — so the CP-9 breakdown rendered the raw
 * `true_false` key beside a card that spelled it correctly on the same page.
 * The back-office library list keeps its own wording deliberately.
 */
export const QUESTION_TYPE_LABELS: Record<string, string> = {
  mcq: "Multiple choice",
  mcq_multi: "Multi-select",
  true_false: "True/false",
  open_ended: "Written",
  // EF-11 (Build B): one noun here too — "File upload" on every surface.
  file_upload: "File upload",
  code: "Coding",
  // EF-9: a 1–5 personality / soft-skills statement — never "question" with
  // a right answer, so never "Multiple choice" either.
  likert: "Self-description",
};

/**
 * The AUTHORING-side wording for the same five types — the results Detail
 * answer rows and both of the editor's question panels.
 *
 * Deliberately a second map rather than a reuse of `QUESTION_TYPE_LABELS`
 * above: these surfaces say "Multi-correct" / "Code" where the results
 * breakdown says "Multi-select" / "Coding", and unifying the wording is a copy
 * decision, not a refactor. What was NOT deliberate is that the same five
 * strings were spelled out as three identical ternary chains, each falling
 * through to "Code" — so a sixth `QuestionType` would have rendered as
 * "Coding" on all three with no type error, which is precisely the drift the
 * docstring above records CP-7 causing. EF-10 had to touch all three by hand
 * this cycle (SIMP-D, 2026-09-12 pre-ship).
 *
 * `Record<QuestionType, string>` is the point: a new type is a compile error
 * here instead of a wrong label there.
 */
export const AUTHORING_QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  mcq: "Multiple choice",
  mcq_multi: "Multi-correct",
  true_false: "True/false",
  open_ended: "Written",
  file_upload: "File upload",
  code: "Code",
  likert: "Self-description",
};

/**
 * EF-9 — mirror of `backend/soft_skills/catalog.LIKERT_LABELS` order: the
 * candidate's answer is the VALUE "1"–"5", the label at that index is what
 * they read. The labels themselves always come from the server (`options`).
 */
export function likertValueFor(index: number): string {
  return String(index + 1);
}

export function likertIndexOf(value: string | null | undefined): number | null {
  if (value == null) return null;
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 && n <= 5 ? n - 1 : null;
}

/** The label, tolerating a snapshot whose `type` is typed as a bare string. */
export function authoringQuestionTypeLabel(type: string): string {
  return (
    AUTHORING_QUESTION_TYPE_LABELS[type as QuestionType] ??
    AUTHORING_QUESTION_TYPE_LABELS.code
  );
}

/**
 * Whether a question-type filter is worth offering to this workspace.
 *
 * `file_upload` is a PLATFORM capability (EF-11), not a plan lock: while the
 * server says it is unavailable no question can carry the type, so the pill can
 * never narrow anything — the same reasoning `QuestionsFilterDrawer` already
 * applies to its availability facet ("a filter that can never narrow anything
 * is noise"), and the create page already hides the matching add button.
 * B5, 2026-09-13 browser QA.
 *
 * Only the OFFER is gated. A stored filter of that type still resolves
 * server-side, and "All" always clears it.
 */
export function isTypeFilterOffered(
  type: QuestionType | "all",
  platform: { fileUploadQuestions: boolean },
): boolean {
  return type !== "file_upload" || platform.fileUploadQuestions;
}

/**
 * FEAT-14 — the line a template card and its preview carry when the template
 * holds written questions: nothing grades them, so a test built from it ends
 * "Awaiting review" until a recruiter scores them (EF-5). Null at zero, so the
 * overwhelming majority of templates read as before. Placeholder wording until
 * Emilia signs off (CLAUDE.md: wording is hers).
 */
export function writtenQuestionsNote(count: number): string | null {
  if (count <= 0) return null;
  return count === 1
    ? "1 written question, scored by hand"
    : `${count} written questions, scored by hand`;
}
